import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, CreditCard, BarChart2, FileText, Users, AlertCircle, Skull, ArrowRight } from 'lucide-react';
import apiService from '../services/apiService';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import ActionCenter from '../components/dashboard/ActionCenter';

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const QUICK_ACTIONS = [
  { label: 'Add Member',  icon: UserPlus,   path: '/members',  style: 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200' },
  { label: 'Payments',    icon: CreditCard, path: '/payments', style: 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200' },
  { label: 'Analytics',   icon: BarChart2,  path: '/analytics',style: 'bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200' },
  { label: 'Notes',       icon: FileText,   path: '/notes',    style: 'bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200' },
  { label: 'All Members', icon: Users,      path: '/members',  style: 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200' },
];

const fmt = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v ?? 0);

export default function DashboardPage() {
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState({});
  const [trends, setTrends] = useState(null);
  const [attentionItems, setAttentionItems] = useState([]);
  const [overdueAccounts, setOverdueAccounts] = useState([]);
  const [deceasedRecords, setDeceasedRecords] = useState([]);
  const [recentContributions, setRecentContributions] = useState([]);

  useEffect(() => {
    (async () => {
      try { setDashboardStats(await apiService.getDashboardStats()); } catch {}
      try { setTrends(await apiService.getDashboardTrends()); } catch {}
      try { setAttentionItems((await apiService.getDashboardAttention()).items || []); } catch {}
      try { setOverdueAccounts(await apiService.getOverduePayments()); } catch {}
      try { setDeceasedRecords(await apiService.getDeceasedRecords()); } catch {}
      try { setRecentContributions(await apiService.getPayments()); } catch {}
    })();
  }, []);

  return (
    <div className="p-3 sm:p-6">
      {/* Page header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">{getGreeting()}</p>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 leading-none">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">AL-IMAN Funeral Management System — real-time overview</p>
        </div>

        {/* Quick action chips */}
        <div className="flex flex-wrap gap-1.5 sm:justify-end">
          {QUICK_ACTIONS.map(({ label, icon: Icon, path, style }) => (
            <button
              key={label}
              onClick={() => navigate(path)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all duration-150 ${style}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <DashboardHeader stats={dashboardStats} trends={trends} />
      <ActionCenter
        items={attentionItems}
        onNavigate={(item) => {
          if (item.memberId) navigate(`/members/${item.memberId}`);
          else navigate(item.targetTab === 'payments' ? '/payments' : '/members');
        }}
      />

      {/* Bottom 3 widgets */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Overdue Accounts */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 flex flex-col overflow-hidden">
          <div className="bg-red-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-white" />
              <span className="text-white font-semibold text-sm">Overdue Accounts</span>
            </div>
            <button onClick={() => navigate('/payments')}
              className="text-white/80 hover:text-white text-xs flex items-center gap-1 transition-colors">
              View All <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="flex-1 divide-y divide-slate-100">
            {overdueAccounts.slice(0, 5).map(r => {
              const total = parseFloat(r.amount) * parseInt(r.overdue_months, 10);
              return (
                <button key={r.member_id}
                  onClick={() => navigate(`/members/${r.member_id}`)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-red-50 transition-colors text-left">
                  <div>
                    <p className="text-sm font-medium text-slate-800 leading-tight">{r.member_name}</p>
                    <p className="text-xs text-slate-400">{r.membership_ref}</p>
                  </div>
                  <span className="text-red-600 font-semibold text-sm">{fmt(-total)}</span>
                </button>
              );
            })}
            {overdueAccounts.length === 0 && (
              <p className="text-slate-400 text-sm text-center py-8">No overdue accounts</p>
            )}
          </div>
          {overdueAccounts.length > 5 && (
            <div className="border-t border-slate-100 px-4 py-2.5 text-center">
              <button onClick={() => navigate('/payments')} className="text-slate-400 text-xs hover:text-slate-600 transition-colors">
                +{overdueAccounts.length - 5} more
              </button>
            </div>
          )}
        </div>

        {/* Deceased Members */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 flex flex-col overflow-hidden">
          <div className="bg-slate-800 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skull className="w-4 h-4 text-white" />
              <span className="text-white font-semibold text-sm">Deceased Members</span>
            </div>
            <button onClick={() => navigate('/deceased')}
              className="text-white/80 hover:text-white text-xs flex items-center gap-1 transition-colors">
              View All <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="flex-1 divide-y divide-slate-100">
            {deceasedRecords.slice(0, 5).map(r => (
              <button key={r.id}
                onClick={() => navigate('/deceased')}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left">
                <div>
                  <p className="text-sm font-medium text-slate-800 leading-tight">{r.deceased_name}</p>
                  <p className="text-xs text-slate-400">
                    {new Date(r.date_of_death).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${r.was_primary_member ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'}`}>
                  {r.was_primary_member ? 'Primary' : 'Family'}
                </span>
              </button>
            ))}
            {deceasedRecords.length === 0 && (
              <p className="text-slate-400 text-sm text-center py-8">No deceased records</p>
            )}
          </div>
          {deceasedRecords.length > 5 && (
            <div className="border-t border-slate-100 px-4 py-2.5 text-center">
              <button onClick={() => navigate('/deceased')} className="text-slate-400 text-xs hover:text-slate-600 transition-colors">
                +{deceasedRecords.length - 5} more
              </button>
            </div>
          )}
        </div>

        {/* Recent Contributions */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 flex flex-col overflow-hidden">
          <div className="bg-violet-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-white" />
              <span className="text-white font-semibold text-sm">Recent Contributions</span>
            </div>
            <button onClick={() => navigate('/contributions-overview')}
              className="text-white/80 hover:text-white text-xs flex items-center gap-1 transition-colors">
              View All <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="flex-1 divide-y divide-slate-100">
            {recentContributions.slice(0, 5).map(r => (
              <button key={r.id}
                onClick={() => r.member_id ? navigate(`/members/${r.member_id}`) : navigate('/contributions-overview')}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-violet-50 transition-colors text-left">
                <div>
                  <p className="text-sm font-medium text-slate-800 leading-tight">{r.member_name}</p>
                  <p className="text-xs text-slate-400">
                    {r.paid_date ? new Date(r.paid_date).toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'2-digit' }) : '—'}
                    {r.frequency ? ` · ${r.frequency}` : ''}
                  </p>
                </div>
                <span className="text-emerald-600 font-semibold text-sm">{fmt(r.amount)}</span>
              </button>
            ))}
            {recentContributions.length === 0 && (
              <p className="text-slate-400 text-sm text-center py-8">No contributions yet</p>
            )}
          </div>
          {recentContributions.length > 5 && (
            <div className="border-t border-slate-100 px-4 py-2.5 text-center">
              <button onClick={() => navigate('/contributions-overview')} className="text-slate-400 text-xs hover:text-slate-600 transition-colors">
                +{recentContributions.length - 5} more
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
